import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { useNotificationsStore } from '@/lib/stores/notifications';
import { resetPassword } from '@/lib/supabase/auth';
import { supabase } from '@/lib/supabase/client';
import { getOAuthRedirectUrl } from '@/lib/supabase/auth';
import { Bell, Mail, Lock, Globe, Smartphone, Save, Check, Eye, Link2, Palette, Clock, AlertCircle } from 'lucide-react';
import type { NotificationPreferencesData } from '@/lib/shared-types/api/endpoints/notifications';

interface UserSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  inAppNotifications: boolean;
  commentNotifications: boolean;
  streamNotifications: boolean;
  marketingEmails: boolean;
  timezone: string;
  language: string;
  theme?: 'light' | 'dark' | 'auto';
  profileVisibility?: 'public' | 'private' | 'friends';
  allowComments?: boolean;
}

interface SectionState {
  [key: string]: boolean;
}

const NOTIFICATION_EVENTS = [
  { key: 'streamLive', label: 'Stream Live', description: 'When streams you follow go live', category: 'stream' },
  { key: 'newComment', label: 'New Comments', description: 'When someone comments on your streams', category: 'social' },
  { key: 'mention', label: 'Mentions', description: 'When someone mentions you in a comment', category: 'social' },
  { key: 'like', label: 'Likes', description: 'When someone likes your content', category: 'social' },
  { key: 'invite', label: 'Invites', description: 'When you receive stream invitations', category: 'system' },
  { key: 'system', label: 'System', description: 'System announcements and updates', category: 'system' },
] as const;

const CHANNELS = [
  { key: 'email', label: 'Email', Icon: Mail },
  { key: 'push', label: 'Push', Icon: Smartphone },
  { key: 'inApp', label: 'In-App', Icon: Bell },
] as const;

const SECTIONS = [
  { id: 'notifications', label: 'Notifications', Icon: Bell },
  { id: 'email', label: 'Email Prefs', Icon: Mail },
  { id: 'appearance', label: 'Appearance', Icon: Palette },
  { id: 'regional', label: 'Regional', Icon: Globe },
  { id: 'privacy', label: 'Privacy', Icon: Eye },
  { id: 'security', label: 'Security', Icon: Lock },
  { id: 'connected', label: 'Connected', Icon: Link2 },
];

export default function SettingsPage() {
  const { user } = useAuthStore();
  const { preferences, fetchPreferences, updatePreferences, requestPushPermission, pushPermission } = useNotificationsStore();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [savedSections, setSavedSections] = useState<SectionState>({});
  const [activeSection, setActiveSection] = useState('notifications');
  
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferencesData | null>(null);
  const [isPrefsLoading, setIsPrefsLoading] = useState(false);
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietHoursStart, setQuietHoursStart] = useState('22:00');
  const [quietHoursEnd, setQuietHoursEnd] = useState('08:00');
  const [quietHoursTimezone, setQuietHoursTimezone] = useState('UTC');
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestFrequency, setDigestFrequency] = useState<'daily' | 'weekly'>('daily');
  const [digestTime, setDigestTime] = useState('09:00');

  const fetchNotificationPrefs = useCallback(async () => {
    setIsPrefsLoading(true);
    try {
      const response = await api.get<{ preferences: NotificationPreferencesData }>('api/notifications/preferences');
      if (response.success && response.data?.preferences) {
        const prefs = response.data.preferences;
        setNotificationPrefs(prefs);
        setQuietHoursEnabled(prefs.quietHours?.enabled || false);
        setQuietHoursStart(prefs.quietHours?.startTime || '22:00');
        setQuietHoursEnd(prefs.quietHours?.endTime || '08:00');
        setQuietHoursTimezone(prefs.quietHours?.timezone || 'UTC');
        setDigestEnabled(prefs.digest?.enabled || false);
        setDigestFrequency(prefs.digest?.frequency || 'daily');
        setDigestTime(prefs.digest?.time || '09:00');
      }
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    } finally {
      setIsPrefsLoading(false);
    }
  }, []);

  const saveNotificationPrefs = useCallback(async () => {
    if (!notificationPrefs) return;
    setSavingSection('Notifications');
    try {
      const updatedPrefs = {
        ...notificationPrefs,
        quietHours: {
          enabled: quietHoursEnabled,
          startTime: quietHoursStart,
          endTime: quietHoursEnd,
          timezone: quietHoursTimezone,
        },
        digest: {
          enabled: digestEnabled,
          frequency: digestFrequency,
          time: digestTime,
        },
      };
      const response = await api.put('api/notifications/preferences', updatedPrefs);
      if (response.success) {
        setSavedSections(prev => ({ ...prev, ['Notifications']: true }));
        toast.success('Notification preferences saved');
        setTimeout(() => {
          setSavedSections(prev => ({ ...prev, ['Notifications']: false }));
        }, 2000);
      } else {
        toast.error(response.error?.message || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSavingSection(null);
    }
  }, [notificationPrefs, quietHoursEnabled, quietHoursStart, quietHoursEnd, quietHoursTimezone, digestEnabled, digestFrequency, digestTime]);

  const handleRequestPushPermission = async () => {
    const permission = await requestPushPermission();
    if (permission === 'granted') {
      toast.success('Push notifications enabled');
    } else if (permission === 'denied') {
      toast.error('Push notifications blocked. Please enable in browser settings.');
    }
  };

  const toggleEventChannel = (eventKey: string, channel: 'email' | 'push' | 'inApp', enabled: boolean) => {
    if (!notificationPrefs) return;
    setNotificationPrefs(prev => {
      if (!prev) return prev;
      const events = { ...prev.events };
      const event = events[eventKey] || { enabled: true, channels: [] as ('email' | 'push' | 'inApp')[] };
      const channels = enabled
        ? [...new Set([...event.channels, channel])]
        : event.channels.filter(c => c !== channel);
      events[eventKey] = { ...event, channels };
      return { ...prev, events };
    });
  };

  const isEventChannelEnabled = (eventKey: string, channel: 'email' | 'push' | 'inApp'): boolean => {
    if (!notificationPrefs?.events[eventKey]) return channel === 'inApp';
    return notificationPrefs.events[eventKey].channels.includes(channel);
  };

  useEffect(() => {
    fetchSettings();
    fetchNotificationPrefs();
  }, [fetchNotificationPrefs]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<UserSettings>('/api/users/me/settings');
      if (response.success && response.data) {
        setSettings(response.data);
      } else {
        // Default settings if not found
        setSettings(getDefaultSettings());
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setSettings(getDefaultSettings());
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultSettings = (): UserSettings => ({
    emailNotifications: true,
    pushNotifications: true,
    inAppNotifications: true,
    commentNotifications: true,
    streamNotifications: true,
    marketingEmails: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: 'en',
    theme: 'auto',
    profileVisibility: 'public',
    allowComments: true
  });

  const handleSaveSection = async (sectionName: string) => {
    if (!settings) return;
    setSavingSection(sectionName);
    try {
      const response = await api.put('/api/users/me/settings', settings);
      if (response.success) {
        setSavedSections(prev => ({ ...prev, [sectionName]: true }));
        toast.success(`${sectionName} saved successfully`);
        setTimeout(() => {
          setSavedSections(prev => ({ ...prev, [sectionName]: false }));
        }, 2000);
      } else {
        toast.error(response.error?.message || `Failed to save ${sectionName}`);
      }
    } catch (error) {
      console.error(`Failed to save ${sectionName}:`, error);
      toast.error(`Failed to save ${sectionName}`);
    } finally {
      setSavingSection(null);
    }
  };

  const updateSetting = <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
    setSettings(prev => prev ? { ...prev, [key]: value } : null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Failed to load settings</p>
            <Button variant="outline" onClick={fetchSettings} className="mt-4">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="@container/settings max-w-[1800px] w-full mx-auto px-3 py-4 @[740px]/settings:px-4 @[740px]/settings:py-5 @[1100px]/settings:px-6 @[1100px]/settings:py-6 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
      </div>

      {/* Settings Layout: Sidebar + Content */}
      <div className="flex flex-col @[740px]/settings:flex-row @[740px]/settings:items-start @[740px]/settings:gap-6">

        {/* Nav: horizontal pill strip on mobile, vertical sidebar on desktop */}
        <nav className="flex @[740px]/settings:flex-col gap-1 overflow-x-auto pb-2 @[740px]/settings:overflow-visible @[740px]/settings:pb-0 @[740px]/settings:w-56 @[740px]/settings:shrink-0">
          {SECTIONS.map(({ id, label, Icon }) => (
            <Button
              key={id}
              variant="ghost"
              onClick={() => setActiveSection(id)}
              className={`justify-start shrink-0 @[740px]/settings:w-full ${activeSection === id ? 'bg-accent text-accent-foreground' : ''}`}
            >
              <Icon className="h-4 w-4 mr-2 shrink-0" />
              {label}
            </Button>
          ))}
        </nav>

        {/* Active Section Content */}
        <div className="flex-1 min-w-0">

          {/* Notifications */}
          {activeSection === 'notifications' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Choose how and when you want to receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {isPrefsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              {/* Channel Status */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Notification Channels</h3>
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  {CHANNELS.map(({ key, label, Icon }) => (
                    <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="flex items-center gap-2">
                            <Label className="font-medium">{label}</Label>
                            {key === 'push' && (
                              pushPermission === 'granted' ? (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">Active</Badge>
                              ) : pushPermission === 'denied' ? (
                                <Badge variant="destructive" className="text-xs">Blocked</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">Not Set</Badge>
                              )
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {key === 'push' && pushPermission === 'denied' ? 'Blocked in browser' : `Receive via ${label.toLowerCase()}`}
                          </p>
                        </div>
                      </div>
                      {key === 'push' ? (
                        pushPermission === 'granted' ? (
                          <Switch
                            checked={notificationPrefs?.channels.push ?? false}
                            onCheckedChange={(checked) => {
                              setNotificationPrefs(prev => prev ? { ...prev, channels: { ...prev.channels, push: checked } } : prev);
                            }}
                          />
                        ) : pushPermission === 'denied' ? (
                          <Button variant="outline" size="sm" onClick={() => window.open('chrome://settings/content/notifications', '_blank')}>
                            Settings
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={handleRequestPushPermission}>
                            Enable
                          </Button>
                        )
                      ) : (
                        <Switch
                          checked={notificationPrefs?.channels[key === 'inApp' ? 'inApp' : key as 'email' | 'inApp'] ?? true}
                          onCheckedChange={(checked) => {
                            const channelKey = key === 'inApp' ? 'inApp' : key as 'email' | 'inApp';
                            setNotificationPrefs(prev => prev ? { ...prev, channels: { ...prev.channels, [channelKey]: checked } } : prev);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                {pushPermission === 'denied' && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>Push notifications are blocked. Enable in browser settings to receive push alerts.</span>
                  </div>
                )}
              </div>

              <Separator />

              {/* Event Type Preferences */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Notification Types</h3>
                <p className="text-xs text-muted-foreground">Choose which events trigger notifications for each channel</p>
                
                <div className="space-y-3">
                  {NOTIFICATION_EVENTS.map(({ key, label, description }) => (
                    <div key={key} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <Label className="font-medium">{label}</Label>
                          <p className="text-xs text-muted-foreground">{description}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-4">
                        {CHANNELS.map(({ key: channelKey, label: channelLabel }) => {
                          const channel = channelKey === 'inApp' ? 'inApp' : channelKey as 'email' | 'push';
                          const isEnabled = isEventChannelEnabled(key, channel);
                          const isChannelEnabled = notificationPrefs?.channels[channel] ?? false;
                          return (
                            <div key={channelKey} className="flex items-center gap-2">
                              <Switch
                                checked={isEnabled}
                                disabled={!isChannelEnabled}
                                onCheckedChange={(checked) => toggleEventChannel(key, channel, checked)}
                                id={`${key}-${channelKey}`}
                              />
                              <Label htmlFor={`${key}-${channelKey}`} className="text-sm cursor-pointer">
                                {channelLabel}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Quiet Hours */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-sm font-medium">Quiet Hours</h3>
                  </div>
                  <Switch
                    checked={quietHoursEnabled}
                    onCheckedChange={setQuietHoursEnabled}
                  />
                </div>
                {quietHoursEnabled && (
                  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                    <div className="space-y-2">
                      <Label className="text-xs">Start Time</Label>
                      <Input
                        type="time"
                        value={quietHoursStart}
                        onChange={(e) => setQuietHoursStart(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">End Time</Label>
                      <Input
                        type="time"
                        value={quietHoursEnd}
                        onChange={(e) => setQuietHoursEnd(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Timezone</Label>
                      <Select value={quietHoursTimezone} onValueChange={setQuietHoursTimezone}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UTC">UTC</SelectItem>
                          <SelectItem value="America/New_York">Eastern</SelectItem>
                          <SelectItem value="America/Chicago">Central</SelectItem>
                          <SelectItem value="America/Denver">Mountain</SelectItem>
                          <SelectItem value="America/Los_Angeles">Pacific</SelectItem>
                          <SelectItem value="Africa/Lagos">Lagos</SelectItem>
                          <SelectItem value="Europe/London">London</SelectItem>
                          <SelectItem value="Europe/Paris">Paris</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  During quiet hours, push and in-app notifications will be suppressed.
                </p>
              </div>

              <Separator />

              {/* Digest Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-sm font-medium">Email Digest</h3>
                  </div>
                  <Switch
                    checked={digestEnabled}
                    onCheckedChange={setDigestEnabled}
                  />
                </div>
                {digestEnabled && (
                  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                    <div className="space-y-2">
                      <Label className="text-xs">Frequency</Label>
                      <Select value={digestFrequency} onValueChange={(v) => setDigestFrequency(v as 'daily' | 'weekly')}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Time</Label>
                      <Input
                        type="time"
                        value={digestTime}
                        onChange={(e) => setDigestTime(e.target.value)}
                      />
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Receive a summary of notifications via email instead of individual messages.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={saveNotificationPrefs}
                  disabled={savingSection === 'Notifications' || isPrefsLoading}
                >
                  {savingSection === 'Notifications' ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : savedSections['Notifications'] ? (
                    <Check className="h-4 w-4 mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {savingSection === 'Notifications' ? 'Saving...' : savedSections['Notifications'] ? 'Saved' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
          )}

          {/* Email Preferences */}
          {activeSection === 'email' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Preferences
          </CardTitle>
          <CardDescription>
            Manage your email subscription preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Marketing Emails</Label>
              <p className="text-sm text-muted-foreground">
                Receive updates about new features and announcements
              </p>
            </div>
            <Switch
              checked={settings.marketingEmails}
              onCheckedChange={(checked) => updateSetting('marketingEmails', checked)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleSaveSection('Email')}
              disabled={savingSection === 'Email'}
            >
              {savingSection === 'Email' ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : savedSections['Email'] ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {savingSection === 'Email' ? 'Saving...' : savedSections['Email'] ? 'Saved' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
          )}

          {/* Appearance */}
          {activeSection === 'appearance' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Appearance
          </CardTitle>
          <CardDescription>
            Customize your appearance preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="theme">Theme</Label>
            <Select
              value={settings.theme || 'auto'}
              onValueChange={(v) => updateSetting('theme', v as 'light' | 'dark' | 'auto')}
            >
              <SelectTrigger id="theme">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="auto">Auto (Match system)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Choose how you want the interface to appear
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleSaveSection('Theme')}
              disabled={savingSection === 'Theme'}
            >
              {savingSection === 'Theme' ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : savedSections['Theme'] ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {savingSection === 'Theme' ? 'Saving...' : savedSections['Theme'] ? 'Saved' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
          )}

          {/* Regional */}
          {activeSection === 'regional' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Regional Settings
          </CardTitle>
          <CardDescription>
            Customize your regional preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={settings.timezone || ''}
              onValueChange={(v) => updateSetting('timezone', v)}
            >
              <SelectTrigger id="timezone">
                <SelectValue placeholder="Select timezone…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="America/New_York">Eastern (America/New_York)</SelectItem>
                <SelectItem value="America/Chicago">Central (America/Chicago)</SelectItem>
                <SelectItem value="America/Denver">Mountain (America/Denver)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific (America/Los_Angeles)</SelectItem>
                <SelectItem value="Africa/Lagos">Lagos (Africa/Lagos)</SelectItem>
                <SelectItem value="Europe/London">London (Europe/London)</SelectItem>
                <SelectItem value="Europe/Paris">Paris (Europe/Paris)</SelectItem>
                <SelectItem value="Asia/Tokyo">Tokyo (Asia/Tokyo)</SelectItem>
                <SelectItem value="Australia/Sydney">Sydney (Australia/Sydney)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Select your timezone for accurate stream schedule display
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="language">Language</Label>
            <Select
              value={settings.language || 'en'}
              onValueChange={(v) => updateSetting('language', v)}
            >
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español (Spanish)</SelectItem>
                <SelectItem value="fr">Français (French)</SelectItem>
                <SelectItem value="de">Deutsch (German)</SelectItem>
                <SelectItem value="it">Italiano (Italian)</SelectItem>
                <SelectItem value="pt">Português (Portuguese)</SelectItem>
                <SelectItem value="ja">日本語 (Japanese)</SelectItem>
                <SelectItem value="zh">中文 (Chinese)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Choose your preferred language for the interface
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleSaveSection('Regional')}
              disabled={savingSection === 'Regional'}
            >
              {savingSection === 'Regional' ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : savedSections['Regional'] ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {savingSection === 'Regional' ? 'Saving...' : savedSections['Regional'] ? 'Saved' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
          )}

          {/* Privacy */}
          {activeSection === 'privacy' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Privacy Settings
          </CardTitle>
          <CardDescription>
            Control who can see your profile and interact with you
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profileVisibility">Profile Visibility</Label>
            <Select
              value={settings.profileVisibility || 'public'}
              onValueChange={(v) => updateSetting('profileVisibility', v as 'public' | 'private' | 'friends')}
            >
              <SelectTrigger id="profileVisibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public — Anyone can view your profile</SelectItem>
                <SelectItem value="friends">Followers Only — Only your followers can view</SelectItem>
                <SelectItem value="private">Private — Only you can view your profile</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Allow Comments</Label>
              <p className="text-sm text-muted-foreground">
                Let others comment on your content
              </p>
            </div>
            <Switch
              checked={settings.allowComments ?? true}
              onCheckedChange={(checked) => updateSetting('allowComments', checked)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => handleSaveSection('Privacy')}
              disabled={savingSection === 'Privacy'}
            >
              {savingSection === 'Privacy' ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : savedSections['Privacy'] ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {savingSection === 'Privacy' ? 'Saving...' : savedSections['Privacy'] ? 'Saved' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
          )}

          {/* Security */}
          {activeSection === 'security' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Account & Security
          </CardTitle>
          <CardDescription>
            Manage your account security settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3">
            <Button variant="outline" className="w-full" onClick={async () => {
              try {
                if (!user?.email) {
                  toast.error('No email address found for your account.');
                  return;
                }
                await resetPassword(user.email);
                toast.success('Password reset email sent to your inbox');
              } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : String(err);
                if (msg.includes('rate limit') || msg.includes('429')) {
                  toast.error('Too many requests — please wait a few minutes before trying again');
                } else {
                  toast.error('Failed to send reset email');
                }
              }
            }}>
              Change Password
            </Button>
            <Button variant="outline" className="w-full" disabled>
              Enable Two-Factor Authentication
              <Badge variant="secondary" className="ml-2 text-xs font-normal">Coming soon</Badge>
            </Button>
            <Button variant="outline" className="w-full" disabled>
              View Active Sessions
              <Badge variant="secondary" className="ml-2 text-xs font-normal">Coming soon</Badge>
            </Button>
          </div>
        </CardContent>
      </Card>
          )}

          {/* Connected Accounts */}
          {activeSection === 'connected' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Connected Accounts
          </CardTitle>
          <CardDescription>
            Manage your connected social media and service accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-md">
            <div className="space-y-0.5">
              <Label className="font-medium">Google Account</Label>
              <p className="text-sm text-muted-foreground">
                Not connected
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                await supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: { redirectTo: getOAuthRedirectUrl('/auth/callback') }
                });
              } catch {
                toast.error('Failed to connect account');
              }
            }}>
              Connect
            </Button>
          </div>
          <div className="flex items-center justify-between p-3 border rounded-md">
            <div className="space-y-0.5">
              <Label className="font-medium">Facebook Account</Label>
              <p className="text-sm text-muted-foreground">
                Not connected
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                await supabase.auth.signInWithOAuth({
                  provider: 'facebook',
                  options: { redirectTo: getOAuthRedirectUrl('/auth/callback') }
                });
              } catch {
                toast.error('Failed to connect account');
              }
            }}>
              Connect
            </Button>
          </div>
        </CardContent>
      </Card>
          )}

        </div>
      </div>
    </div>
  );
}
